import { z } from 'zod';
/**
 * A tarefa do Perto de Mim (PA2 do desenho das Páginas Amarelas, aprovado em 26/10): o Secretário
 * pede a até 3 negócios por você, espera as respostas sem pesar no servidor (cada tarefa guarda
 * quando olhar de novo), você escolhe e ele confirma na sua agenda. Quem manda a mensagem pelo
 * WhatsApp é você, do seu número; a resposta volta pelo leitor de avisos do Android ou colada.
 */
export const PERTO_TASK_PLACES_MAX = 3;
/** Tarefas abertas ao mesmo tempo por pessoa (plano grátis). */
export const PERTO_TASKS_OPEN_MAX = 5;
/** Lembrete ao negócio que não respondeu: depois de 3 horas úteis, no máximo dois. */
export const PERTO_TASK_REMIND_HOURS = 3;
export const PERTO_TASK_REMINDERS_MAX = 2;
/** Sem resposta nenhuma em 24 h, oferece o próximo da lista; em 48 h, encerra. */
export const PERTO_TASK_NEXT_HOURS = 24;
export const PERTO_TASK_CLOSE_HOURS = 48;
export const PERTO_TASK_STATES = [
    'pedindo',
    'esperando',
    'escolhendo',
    'confirmado',
    'encerrada',
];
export const PERTO_TASK_STATE_LABEL = {
    pedindo: 'Mensagens prontas',
    esperando: 'Esperando respostas',
    escolhendo: 'Você escolhe',
    confirmado: 'Confirmado',
    encerrada: 'Encerrada',
};
/**
 * a_pedir: a mensagem está pronta, falta você enviar; pedido: você enviou; respondeu: a resposta
 * chegou; escolhido / dispensado: depois da escolha; sem_resposta: encerrou sem resposta.
 */
export const PERTO_TASK_PLACE_STATES = [
    'a_pedir',
    'pedido',
    'respondeu',
    'escolhido',
    'dispensado',
    'sem_resposta',
];
export const pertoTaskPlaceSchema = z.object({
    placeId: z.string(),
    name: z.string(),
    label: z.string(),
    /** O número em formato internacional, para o wa.me e para reconhecer a resposta. */
    phone: z.string().nullable(),
    /** O negócio está no Secretário (a conversa é por dentro, sem WhatsApp). */
    onSec: z.boolean(),
    state: z.enum(PERTO_TASK_PLACE_STATES),
    /** A mensagem só deste negócio (o pedido e, depois, o lembrete). */
    text: z.string(),
    askedAt: z.iso.datetime().nullable(),
    reminders: z.number().int(),
    /** O lembrete a este negócio está pronto para você enviar (sem resposta há 3 horas úteis). */
    nudge: z.boolean(),
    reply: z
        .object({
        text: z.string(),
        at: z.iso.datetime(),
        /** O dia e a hora que o negócio propôs, quando a resposta traz os dois ("quinta às 14h"). */
        suggested: z.iso.datetime().optional(),
    })
        .nullable(),
});
export const pertoTaskSchema = z.object({
    id: z.string(),
    /** O que a pessoa quer ("Eletricista para trocar o chuveiro sábado de manhã"). */
    title: z.string(),
    state: z.enum(PERTO_TASK_STATES),
    places: z.array(pertoTaskPlaceSchema),
    /** Quantos da lista ainda podem entrar se ninguém responder. */
    more: z.number().int(),
    /** Quando o Secretário olha de novo (o agendador acorda só esta tarefa). */
    nextCheckAt: z.iso.datetime().nullable(),
    /** O que ficou combinado (o negócio escolhido e o horário, se houver). */
    chosen: z
        .object({ placeId: z.string(), name: z.string(), when: z.iso.datetime().nullable() })
        .nullable(),
    /** O resumo do fim ("Encerrei: 2 responderam; você escolheu…"). */
    summary: z.string().nullable(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
});
export const createPertoTaskSchema = z.object({
    title: z.string().trim().min(2).max(120),
    text: z.string().trim().min(2).max(1000),
    /** Os lugares (ids do Perto de Mim), até 3; o resto da busca fica de reserva. */
    placeIds: z.array(z.string().min(1).max(64)).min(1).max(PERTO_TASK_PLACES_MAX),
    reserveIds: z.array(z.string().min(1).max(64)).max(6).optional(),
});
export const pertoTaskReplySchema = z.object({
    placeId: z.string().min(1).max(64),
    text: z.string().trim().min(1).max(2000),
    /** Quando a resposta chegou (o aviso do WhatsApp); sem isso, agora. */
    at: z.iso.datetime().optional(),
});
export const pertoTaskChooseSchema = z.object({
    placeId: z.string().min(1).max(64),
    /** O horário combinado, quando houver: entra na sua agenda. */
    when: z.iso.datetime().nullable().optional(),
});
/** Só os dígitos; o número do Brasil sem o país ganha o 55. */
export function phoneDigitsOf(raw) {
    const digits = (raw ?? '').replace(/\D/g, '');
    if (digits.length < 10)
        return null;
    return digits.length <= 11 ? `55${digits}` : digits;
}
/**
 * A resposta é deste negócio? O aviso do WhatsApp traz o nome salvo no telefone ou o número:
 * vale o número (os últimos 8 dígitos) ou o nome, sem acento e sem caixa.
 */
export function replyMatches(from, place) {
    const digits = from.replace(/\D/g, '');
    if (digits.length >= 8 && place.phone && place.phone.endsWith(digits.slice(-8)))
        return true;
    const fold = (text) => text
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/[^a-z0-9 ]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
    const who = fold(from.replace(/^~/, ''));
    const name = fold(place.name);
    return (who.length >= 4 &&
        name.length >= 4 &&
        (who === name || who.includes(name) || name.includes(who)));
}
